import _, { forEach, isObject } from 'lodash';
import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { useFormContext } from 'react-hook-form';
import { useEffect } from 'react';
import useUiToasts, { SUCCESS } from '../stores/ToastStore';
import { createAlertConfig } from '../stores/AlertsStore';
import { useNavigate } from './PgmUtilities';

export const SORT_ASC = 'asc';
export const SORT_DESC = 'desc';

export const TEXT_FIELD_TYPE_TEXT = 'text';
export const TEXT_FIELD_TYPE_PASSWORD = 'password';
export const TEXT_FIELD_TYPE_NUMBER = 'number';
export const MANUAL_ERROR_TYPE = 'manual';
export const AUTO_COMPLETE_DEFAULT = 'new-password';
export const MATERIAL_UI_SELECT_PROPS = {};
export const USE_FORM_DEFAULT_OPTIONS = {
    mode: 'all',
    shouldUnregister: true,
};

// Generic Validation Messages
export const GENERIC_REQUIRED_MESSAGE = i18n._(t`Value is required`);

export const DEFAULT_STYLE_INPUT_PROPS = {
    size: 'small',
    margin: 'none',
};

export const DEFAULT_INPUT_PROPS = _.merge(_.clone(DEFAULT_STYLE_INPUT_PROPS), {
    defaultValue: '',
});

export const DEFAULT_REQUIRED_INPUT_PROPS = _.merge(_.clone(DEFAULT_INPUT_PROPS), {
    rules: { required: GENERIC_REQUIRED_MESSAGE },
});

export const EMAIL_REGEX =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@(((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?))|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
export const IP_REGEX =
    /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
export const NETWORK_PREFIX_LENGTH_REGEX = /^([0-9]|[1-2][0-9]|3[0-2])$/;
export const DNS_REGEX =
    /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)(;((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?))*$/;

export function unpackValidationErrors(response) {
    const apiValidationPath = 'details.errors';
    const apiV2ValidationPath = 'validations';
    const messageValidationPath = 'message';

    return _.get(
        response,
        apiValidationPath,
        _.get(response, apiV2ValidationPath, _.get(response, messageValidationPath))
    );
}

export function fieldHasErrors(fieldName, errors) {
    return !_.isEmpty(errors[fieldName]);
}

// A custom hook that builds on useLocation to parse
// the query string for you.
export function useQueryStringHelper(location) {
    return new URLSearchParams(location.search);
}

export function getIdFromQueryString(query) {
    const filterParamSeparator = '|';
    const filterKeyValueSeparator = ':';
    const idField = '_id';
    const filterState = query.get('filterState');
    if (!filterState) {
        return undefined;
    }

    const states = filterState.split(filterParamSeparator);
    return _.reduce(
        states,
        (result, next) => {
            const parts = next.split(filterKeyValueSeparator);
            if (_.first(parts) === idField) {
                result = _.last(parts);
            }

            return result;
        },
        undefined
    );
}

export function isEmailValid(email) {
    if (_.isEmpty(email)) {
        return false;
    }

    return EMAIL_REGEX.test(_.trim(email));
}

export function isIpValid(ip) {
    if (_.isEmpty(ip)) {
        return false;
    }

    return IP_REGEX.test(_.trim(ip));
}

export function isNetworkPrefixLengthValid(nwPreLen) {
    if (_.isNull(nwPreLen)) {
        return false;
    }

    return NETWORK_PREFIX_LENGTH_REGEX.test(_.trim(nwPreLen));
}

export function isDNSValid(mask) {
    if (_.isNull(mask)) {
        return false;
    }

    return DNS_REGEX.test(_.trim(mask));
}

export function useFieldWatch(fieldName) {
    const { watch } = useFormContext();

    return watch(fieldName);
}

export function removeEmptyValues(objectToEmpty) {
    const removeEmpty = (clean) => {
        return _.transform(clean, (result, value, key) => {
            const isCollection = _.isObject(value);
            const cleaned = isCollection ? removeEmpty(value) : value;

            if (isCollection && _.isEmpty(cleaned) && !_.isBoolean(value)) {
                return;
            }

            if (_.isEmpty(cleaned) && !_.isBoolean(value)) {
                return;
            }

            return _.isArray(result) ? result.push(cleaned) : (result[key] = cleaned);
        });
    };

    return _.isObject(objectToEmpty) ? removeEmpty(objectToEmpty) : objectToEmpty;
}

const successToastObject = createAlertConfig(SUCCESS, i18n._(t`Successfully Deleted`));

export function useSuccessfulAction(successMessage) {
    const [, uiToastActions] = useUiToasts();
    const navigate = useNavigate();
    const successToast = successMessage ? createAlertConfig(SUCCESS, successMessage) : successToastObject;

    const navigateAway = (goTo, reloadFunc) => {
        if (_.isEmpty(goTo)) {
            return reloadFunc();
        }

        return navigate(goTo, { replace: true });
    };

    return (response, onActionGoTo, refresherFunc = () => ({})) => {
        uiToastActions.createToast(_.merge(successToast, { createOnNavigate: !_.isEmpty(onActionGoTo) }));
        navigateAway(onActionGoTo, refresherFunc);
        return response;
    };
}

export const getSuccessToast = (action, successMessageObj) => {
    const successMessage = successMessageObj[action];
    if (_.isEmpty(successMessage)) {
        return createAlertConfig(SUCCESS, i18n._(t`Please check do what action`));
    }

    return createAlertConfig(SUCCESS, successMessage);
};

/**
 * Sets field-level validations on a form.
 *
 * @param {Object.<string, { message: string }>} validationErrors An object where the key is the field name and the
 *      value is an object containing a `message` property.
 * @param {(name: string, error: any) => void} [setErrorFn] The `setError` function from formMethods. If the form
 *      is in a context, this parameter is optional
 */
export function useValidationErrors(validationErrors, setErrorFn) {
    const formContext = useFormContext();
    const setError = setErrorFn || formContext?.setError;

    if (typeof setError !== 'function')
        throw new Error('Missing setErrorFn. If not provided, hook must fall within a FormProvider element');

    useEffect(() => {
        if (!validationErrors || !isObject(validationErrors)) return;

        forEach(validationErrors, (e, k) => setError(k, { message: e?.message }));
    }, [setError, validationErrors]);
}

export function formatMAC(unFormatted) {
    const delimiter = ':';
    if (!unFormatted || unFormatted.indexOf(delimiter) > -1) {
        return unFormatted;
    }

    return unFormatted
        .match(/.{1,2}/g)
        .join(delimiter)
        .toUpperCase();
}
